import { create } from 'zustand'
import {
  fetchMedicineCategories,
  fetchSyncFrequencies,
  fetchOnboardingSlides,
  fetchPricingTiers,
  fetchPPPMultipliers,
  fetchFXRates,
  fetchAIMessageTemplates,
  fetchSettingsMenu,
  fetchListFrequencyOptions,
  fetchTimePerListOptions,
  type MedicineCategoryRow,
  type SyncFrequencyRow,
  type OnboardingSlideRow,
  type PricingTierRow,
  type PPPMultiplierRow,
  type FXRateRow,
  type AIMessageTemplateRow,
  type SettingsMenuRow,
  type ListFrequencyOptionRow,
  type TimePerListOptionRow,
} from '../config/api'

/** Convert a user-facing sync frequency to milliseconds */
export function syncFrequencyToMs(frequency?: string): number {
  switch (frequency) {
    case '15min': return 15 * 60 * 1000
    case '30min': return 30 * 60 * 1000
    case '1hour': return 60 * 60 * 1000
    case '4hours': return 4 * 60 * 60 * 1000
    case '12hours': return 12 * 60 * 60 * 1000
    case 'daily': case '24hours': return 24 * 60 * 60 * 1000
    case 'manual': return Infinity
    default: return 60 * 60 * 1000 // default: 1 hour
  }
}

export interface RuntimeConfigState {
  isLoaded: boolean
  isLoading: boolean
  error: string | null
  lastLoadedAt: number | null

  medicineCategories: MedicineCategoryRow[]
  listFrequencyOptions: ListFrequencyOptionRow[]
  timePerListOptions: TimePerListOptionRow[]
  syncFrequencies: SyncFrequencyRow[]
  onboardingSlides: OnboardingSlideRow[]
  pricingTiers: PricingTierRow[]
  pppMultipliers: PPPMultiplierRow[]
  fxRates: FXRateRow[]
  aiMessageTemplates: AIMessageTemplateRow[]
  settingsMenu: SettingsMenuRow[]
  loadConfig: (authToken: string) => Promise<void>
  /** Reload config if data is older than the user's sync frequency */
  refreshIfStale: (authToken: string, syncFrequency?: string) => Promise<void>
  getMedicineCategoriesForType: (pharmacyType: string) => MedicineCategoryRow[]
  getSyncFrequency: (id: string) => SyncFrequencyRow | undefined
  getPricingTier: (id: string) => PricingTierRow | undefined
  getPPPForCountry: (countryCode: string) => PPPMultiplierRow | undefined
  getFXRate: (base: string, target: string) => FXRateRow | undefined
  getPriceForCurrency: (tierId: string, currency: string) => number | undefined
  getVolumePrice: (tierId: string, branchCount: number) => number | undefined
}

export const useRuntimeConfigStore = create<RuntimeConfigState>((set, get) => ({
  isLoaded: false,
  isLoading: false,
  error: null,
  lastLoadedAt: null,

  medicineCategories: [],
  listFrequencyOptions: [],
  timePerListOptions: [],
  syncFrequencies: [],
  onboardingSlides: [],
  pricingTiers: [],
  pppMultipliers: [],
  fxRates: [],
  aiMessageTemplates: [],
  settingsMenu: [],
  loadConfig: async (authToken: string) => {
    set({ isLoading: true, error: null })
    try {
      const [
        medicineCategories,
        listFrequencyOptions,
        timePerListOptions,
        syncFrequencies,
        onboardingSlides,
        pricingTiers,
        pppMultipliers,
        fxRates,
        aiMessageTemplates,
        settingsMenu,
      ] = await Promise.all([
        fetchMedicineCategories(authToken),
        fetchListFrequencyOptions(authToken),
        fetchTimePerListOptions(authToken),
        fetchSyncFrequencies(authToken),
        fetchOnboardingSlides(authToken),
        fetchPricingTiers(authToken),
        fetchPPPMultipliers(authToken),
        fetchFXRates(authToken),
        fetchAIMessageTemplates(authToken),
        fetchSettingsMenu(authToken),
      ])
      
      set({
        isLoaded: true,
        isLoading: false,
        lastLoadedAt: Date.now(),
        medicineCategories,
        listFrequencyOptions,
        timePerListOptions,
        syncFrequencies,
        onboardingSlides,
        pricingTiers,
        pppMultipliers,
        fxRates,
        aiMessageTemplates,
        settingsMenu,
      })
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load config',
      })
    }
  },

  refreshIfStale: async (authToken: string, syncFrequency?: string) => {
    const { lastLoadedAt, isLoading } = get()
    if (isLoading) return
    const staleMs = syncFrequencyToMs(syncFrequency)
    if (staleMs === Infinity) return // manual = never auto-refresh
    const isStale = !lastLoadedAt || (Date.now() - lastLoadedAt) > staleMs
    if (!isStale) return
    await get().loadConfig(authToken)
  },
  
  getMedicineCategoriesForType: (pharmacyType: string) => {
    const { medicineCategories } = get()
    return medicineCategories.filter((c) => c.pharmacy_type === pharmacyType || c.pharmacy_type === 'all')
  },
  
  getSyncFrequency: (id: string) => {
    const { syncFrequencies } = get()
    return syncFrequencies.find((f) => f.id === id)
  },
  
  getPricingTier: (id: string) => {
    const { pricingTiers } = get()
    return pricingTiers.find((t) => t.id === id)
  },
  
  getPPPForCountry: (countryCode: string) => {
    const { pppMultipliers } = get()
    return pppMultipliers.find((p) => p.id === countryCode)
  },
  
  getFXRate: (base: string, target: string) => {
    const { fxRates } = get()
    return fxRates.find((r) => r.base_currency === base && r.target_currency === target)
  },
  
  getPriceForCurrency: (tierId: string, currency: string) => {
    const tier = get().getPricingTier(tierId)
    if (!tier) return undefined
    return tier.prices[currency]
  },
  
  getVolumePrice: (tierId: string, branchCount: number) => {
    const tier = get().getPricingTier(tierId)
    if (!tier) return undefined
    
    const discounts = tier.volume_discounts || []
    for (const discount of discounts) {
      if (branchCount >= discount.minBranches && branchCount <= discount.maxBranches) {
        return discount.usdRate
      }
    }
    return tier.base_usd_rate || undefined
  },
}))
